import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  onSnapshot,
  query,
  where,
  writeBatch,
  Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import type {
  RSVP,
  RSVPWrite,
  RSVPStatus,
  RSVPApprovalStatus,
  UserProfile,
} from '@penticton-dev-events/shared';

// Re-export types for convenience
export type { RSVP, RSVPStatus, RSVPApprovalStatus };

const rsvpCollection = (firestore: Firestore) => collection(firestore, 'rsvps');

/**
 * Normalize legacy RSVP data to include approval fields
 */
const normalizeRSVP = (data: any): RSVP => {
  return {
    id: data.id,
    meetingId: data.meetingId,
    userId: data.userId,
    status: data.status,
    approvalStatus: data.approvalStatus ?? 'approved',
    approvedBy: data.approvedBy,
    approvedAt: data.approvedAt,
    rejectionReason: data.rejectionReason,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    userDisplayName: data.userDisplayName ?? 'Unknown User',
    userEmail: data.userEmail,
    userPhotoURL: data.userPhotoURL,
  };
};

/**
 * Set or update RSVP with approval workflow support
 */
export const setRSVP = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
  status: RSVPStatus,
  requiresApproval: boolean,
  userProfile: Pick<UserProfile, 'displayName' | 'email' | 'photoURL'>,
): Promise<void> => {
  const rsvpId = `${meetingId}_${userId}`;
  const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

  const now = new Date().toISOString();

  // Check if RSVP exists to preserve approval status on updates
  const existingDoc = await getDoc(rsvpRef);
  const existingRsvp = existingDoc.exists() ? normalizeRSVP(existingDoc.data()) : null;

  // Determine approval status
  let approvalStatus: RSVPApprovalStatus;
  let approvedAt: string | undefined;

  if (existingRsvp) {
    // If user is switching TO 'attending' status, check if approval is required
    if (status === 'attending' && existingRsvp.status !== 'attending' && requiresApproval) {
      // Reset to pending when switching to attending on a meeting requiring approval
      approvalStatus = 'pending';
      approvedAt = undefined;
    } else {
      // Preserve existing approval status (e.g., staying as attending, or switching to not_attending)
      approvalStatus = existingRsvp.approvalStatus;
      approvedAt = existingRsvp.approvedAt;
    }
  } else {
    // New RSVP - set based on meeting requirements
    approvalStatus = requiresApproval && status === 'attending' ? 'pending' : 'approved';
    // Auto-approved RSVPs get approvedAt timestamp immediately
    approvedAt = approvalStatus === 'approved' ? now : undefined;
  }

  // Validate required fields
  if (!meetingId || !userId || !status || !approvalStatus) {
    throw new Error('Missing required RSVP fields');
  }

  // Build the RSVP data object, excluding undefined fields (Firestore doesn't allow undefined)
  const rsvpData: any = {
    id: rsvpId,
    meetingId,
    userId,
    status,
    approvalStatus,
    createdAt: existingRsvp?.createdAt ?? now,
    updatedAt: now,
    userDisplayName: userProfile.displayName || 'Unknown User',
  };

  // Only include optional user fields if they have non-empty values
  if (userProfile.email && userProfile.email !== '') {
    rsvpData.userEmail = userProfile.email;
  }
  if (userProfile.photoURL && userProfile.photoURL !== '') {
    rsvpData.userPhotoURL = userProfile.photoURL;
  }

  // Only include approvedAt if it has a value
  if (approvedAt && approvedAt !== '') {
    rsvpData.approvedAt = approvedAt;
  }

  // Only preserve approvedBy and rejectionReason if approval status is not pending
  // (When resetting to pending, we don't include these fields to clear them)
  if (approvalStatus !== 'pending') {
    if (existingRsvp?.approvedBy && existingRsvp.approvedBy !== '') {
      rsvpData.approvedBy = existingRsvp.approvedBy;
    }
    if (existingRsvp?.rejectionReason && existingRsvp.rejectionReason !== '') {
      rsvpData.rejectionReason = existingRsvp.rejectionReason;
    }
  }

  await setDoc(rsvpRef, rsvpData);
};

export const removeRSVP = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
): Promise<void> => {
  const rsvpId = `${meetingId}_${userId}`;
  const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

  await deleteDoc(rsvpRef);
  console.log('RSVP removed:', { meetingId, userId });
};

/**
 * Approve an RSVP (organizer only)
 * Fetches fresh user profile data to ensure we have current user info
 */
export const approveRSVP = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
  approvedByUid: string,
): Promise<void> => {
  const rsvpId = `${meetingId}_${userId}`;
  const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

  const now = new Date().toISOString();

  // Get existing RSVP
  const existingDoc = await getDoc(rsvpRef);
  if (!existingDoc.exists()) {
    throw new Error('RSVP not found');
  }

  const existingRsvp = normalizeRSVP(existingDoc.data());

  // Fetch current user profile to get fresh user data
  const userRef = doc(collection(firestore, 'users'), userId);
  const userDoc = await getDoc(userRef);

  let userDisplayName = existingRsvp.userDisplayName;
  let userEmail = existingRsvp.userEmail;
  let userPhotoURL = existingRsvp.userPhotoURL;

  if (userDoc.exists()) {
    const userData = userDoc.data();
    userDisplayName = userData.displayName || userDisplayName;
    userEmail = userData.email || userEmail;
    userPhotoURL = userData.photoURL || userPhotoURL;
  }

  // Build approval data, excluding undefined fields
  const approvalData: any = {
    id: rsvpId,
    meetingId: existingRsvp.meetingId,
    userId: existingRsvp.userId,
    status: existingRsvp.status,
    approvalStatus: 'approved',
    approvedBy: approvedByUid,
    approvedAt: now,
    // Don't include rejectionReason - omit it entirely to remove from Firestore
    createdAt: existingRsvp.createdAt,
    updatedAt: now,
    userDisplayName,
  };

  // Only include email and photo if they exist
  if (userEmail) {
    approvalData.userEmail = userEmail;
  }
  if (userPhotoURL) {
    approvalData.userPhotoURL = userPhotoURL;
  }

  await setDoc(rsvpRef, approvalData);

  console.log('RSVP approved:', { meetingId, userId, approvedBy: approvedByUid });
};

/**
 * Reject an RSVP (organizer only)
 * Fetches fresh user profile data to ensure we have current user info
 */
export const rejectRSVP = async (
  firestore: Firestore,
  meetingId: string,
  userId: string,
  rejectedByUid: string,
  rejectionReason?: string,
): Promise<void> => {
  const rsvpId = `${meetingId}_${userId}`;
  const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

  const now = new Date().toISOString();

  // Get existing RSVP
  const existingDoc = await getDoc(rsvpRef);
  if (!existingDoc.exists()) {
    throw new Error('RSVP not found');
  }

  const existingRsvp = normalizeRSVP(existingDoc.data());

  // Fetch current user profile to get fresh user data
  const userRef = doc(collection(firestore, 'users'), userId);
  const userDoc = await getDoc(userRef);

  let userDisplayName = existingRsvp.userDisplayName;
  let userEmail = existingRsvp.userEmail;
  let userPhotoURL = existingRsvp.userPhotoURL;

  if (userDoc.exists()) {
    const userData = userDoc.data();
    userDisplayName = userData.displayName || userDisplayName;
    userEmail = userData.email || userEmail;
    userPhotoURL = userData.photoURL || userPhotoURL;
  }

  // Build rejection data object, excluding undefined fields
  const rejectionData: any = {
    id: rsvpId,
    meetingId: existingRsvp.meetingId,
    userId: existingRsvp.userId,
    status: existingRsvp.status,
    approvalStatus: 'rejected',
    approvedBy: rejectedByUid,
    approvedAt: now,
    createdAt: existingRsvp.createdAt,
    updatedAt: now,
    userDisplayName,
  };

  // Only include email and photo if they exist
  if (userEmail) {
    rejectionData.userEmail = userEmail;
  }
  if (userPhotoURL) {
    rejectionData.userPhotoURL = userPhotoURL;
  }

  // Only include rejectionReason if provided
  if (rejectionReason) {
    rejectionData.rejectionReason = rejectionReason;
  }

  await setDoc(rsvpRef, rejectionData);

  console.log('RSVP rejected:', { meetingId, userId, rejectedBy: rejectedByUid, rejectionReason });
};

/**
 * Batch approve multiple RSVPs (organizer only)
 * Note: Does NOT fetch user profiles for performance - assumes RSVPs already have user data
 * If user data is missing, individual approval will backfill it
 */
export const batchApproveRSVPs = async (
  firestore: Firestore,
  meetingId: string,
  userIds: string[],
  approvedByUid: string,
): Promise<void> => {
  if (userIds.length === 0) {
    return;
  }

  const batch = writeBatch(firestore);
  const now = new Date().toISOString();

  // Fetch all RSVPs first to get user data
  const rsvpPromises = userIds.map(userId => {
    const rsvpId = `${meetingId}_${userId}`;
    const rsvpRef = doc(rsvpCollection(firestore), rsvpId);
    return getDoc(rsvpRef);
  });

  const rsvpDocs = await Promise.all(rsvpPromises);

  // Fetch all user profiles
  const userPromises = userIds.map(userId => {
    const userRef = doc(collection(firestore, 'users'), userId);
    return getDoc(userRef);
  });

  const userDocs = await Promise.all(userPromises);

  // Build batch updates with fresh user data
  for (let i = 0; i < userIds.length; i++) {
    const userId = userIds[i];
    const rsvpId = `${meetingId}_${userId}`;
    const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

    const rsvpDoc = rsvpDocs[i];
    if (!rsvpDoc || !rsvpDoc.exists()) {
      console.warn(`RSVP not found for batch approval: ${rsvpId}`);
      continue;
    }

    const existingRsvp = normalizeRSVP(rsvpDoc.data());

    // Get fresh user data
    let userDisplayName = existingRsvp.userDisplayName;
    let userEmail = existingRsvp.userEmail;
    let userPhotoURL = existingRsvp.userPhotoURL;

    const userDoc = userDocs[i];
    if (userDoc && userDoc.exists()) {
      const userData = userDoc.data();
      userDisplayName = userData?.displayName || userDisplayName;
      userEmail = userData?.email || userEmail;
      userPhotoURL = userData?.photoURL || userPhotoURL;
    }

    // Build batch approval data, excluding undefined fields
    const batchApprovalData: any = {
      id: rsvpId,
      meetingId: existingRsvp.meetingId,
      userId: existingRsvp.userId,
      status: existingRsvp.status,
      approvalStatus: 'approved',
      approvedBy: approvedByUid,
      approvedAt: now,
      createdAt: existingRsvp.createdAt,
      updatedAt: now,
      userDisplayName,
    };

    // Only include email and photo if they exist
    if (userEmail) {
      batchApprovalData.userEmail = userEmail;
    }
    if (userPhotoURL) {
      batchApprovalData.userPhotoURL = userPhotoURL;
    }

    batch.set(rsvpRef, batchApprovalData);
  }

  await batch.commit();
  console.log('RSVPs batch approved:', { meetingId, count: userIds.length, approvedBy: approvedByUid });
};

export const subscribeToUserRSVP = (
  firestore: Firestore,
  meetingId: string,
  userId: string,
  onRSVP: (rsvp: RSVP | null) => void,
  onError: (error: Error) => void,
): Unsubscribe => {
  const rsvpId = `${meetingId}_${userId}`;
  const rsvpRef = doc(rsvpCollection(firestore), rsvpId);

  return onSnapshot(
    rsvpRef,
    snapshot => {
      if (snapshot.exists()) {
        onRSVP(normalizeRSVP(snapshot.data()));
      } else {
        onRSVP(null);
      }
    },
    onError,
  );
};

export const subscribeToMeetingRSVPs = (
  firestore: Firestore,
  meetingId: string,
  onRSVPs: (rsvps: RSVP[]) => void,
  onError: (error: Error) => void,
): Unsubscribe => {
  const rsvpsQuery = query(
    rsvpCollection(firestore),
    where('meetingId', '==', meetingId),
  );

  return onSnapshot(
    rsvpsQuery,
    snapshot => {
      const rsvps = snapshot.docs.map(doc => normalizeRSVP(doc.data()));
      onRSVPs(rsvps);
    },
    onError,
  );
};

/**
 * Subscribe to pending RSVPs for a meeting (organizer only)
 */
export const subscribeToPendingRSVPs = (
  firestore: Firestore,
  meetingId: string,
  onRSVPs: (rsvps: RSVP[]) => void,
  onError: (error: Error) => void,
): Unsubscribe => {
  const rsvpsQuery = query(
    rsvpCollection(firestore),
    where('meetingId', '==', meetingId),
    where('approvalStatus', '==', 'pending'),
  );

  return onSnapshot(
    rsvpsQuery,
    snapshot => {
      const rsvps = snapshot.docs.map(doc => normalizeRSVP(doc.data()));
      onRSVPs(rsvps);
    },
    onError,
  );
};
